import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../grid/gridView';
import { Column, ColumnKeys, Dependencies, ScheduleItem } from '../types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import {
  WEB_COMPONENT_CONFIGURATION_PROFILES,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_WEB_ACCESS_CONTROL_SCHEDULER_FORM,
} from '../../../webcomponents.constants';
import { GridConfig } from './GridConfig';
import { GridViewMetadata, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { WebAccessControlSchedulerGridDataProvider } from '../dataProvider/WebAccessControlSchedulerGridDataProvider';
import { GridTitleConfig } from '../../../grid/uiElements/gridViewTitle/types';
import { WebAccessControlSchedulerFormComponent } from '../../webAccessControlSchedulerForm';
import { Rights } from '@nirvana/shared/constants';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { translateFn } from '../../../utils/translator.helper';

export class WebAccessControlSchedulerView extends BaseWebComponentView {
  public dom: {
    $hostElement: HTMLElement;
    $grid: GridViewWebComponent<Column, ColumnKeys>;
    $addScheduleBtn: GzButtonWebComponent;
    $deleteBtn: GzButtonWebComponent;
    $webAccessControlSchedulerForm: WebAccessControlSchedulerFormComponent;
  };
  public dependencies: Dependencies;

  public getDomElements(): void {
    this.dom.$hostElement = this.shadow;
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$addScheduleBtn = this.shadow.querySelector('#addScheduleBtn');
    this.dom.$deleteBtn = this.shadow.querySelector('#deleteBtn');
    this.dom.$webAccessControlSchedulerForm = this.shadow.querySelector(
      WEB_COMPONENT_WEB_ACCESS_CONTROL_SCHEDULER_FORM
    );

    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.dom.$addScheduleBtn.disabled = true;
      this.dom.$deleteBtn.disabled = true;
    }
  }

  public buildView(): void {
    const { viewMetaData } = new GridConfig().getDataForView(this.dependencies);
    this.buildGrid(viewMetaData);
    this.injectDependenciesInChildren();
    this.setTranslations();
    this.buildWebAccessControlSchedulerForm();
    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.dom.$addScheduleBtn.disabled = true;
    }
  }

  private buildGrid(viewMetaData: GridViewMetadata<Column, ColumnKeys>): void {
    const grid = this.dom.$grid;
    grid.titleConfig = new GridTitleConfig(translateFn('Web Access Control Schedulers'));
    const defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
      compactView: viewMetaData.isCompactViewChecked,
      displayedColumns: viewMetaData.columns,
      filters: viewMetaData.filters,
      name: '',
      pageSize: viewMetaData.pageSize,
      type: ViewType.DEFAULT,
      id: viewMetaData.viewId,
    };
    grid.propertiesSet = false;
    grid.dataProvider = new WebAccessControlSchedulerGridDataProvider(this.dependencies);
    grid.sideMenuParent = window.document
      .querySelector(WEB_COMPONENT_CONFIGURATION_PROFILES)
      ?.shadowRoot?.querySelector('.wrapper') as HTMLElement;

    for (const [key, value] of Object.entries(viewMetaData)) {
      grid[key] = value;
    }
    grid.defaultViewProps = defaultViewProps;
    grid.propertiesSet = true;
    grid.injectDependencies(this.dependencies);
    grid.start().catch();
  }

  private buildWebAccessControlSchedulerForm() {
    this.dom.$webAccessControlSchedulerForm.isVisible = false;
  }

  public showWebAccessControlSchedulerForm(webAccessControlSchedule?: ScheduleItem | undefined): void {
    this.dom.$webAccessControlSchedulerForm.propertiesSet = false;
    this.dom.$webAccessControlSchedulerForm.isVisible = true;

    if (webAccessControlSchedule?.subschedules) {
      webAccessControlSchedule.subschedules = Object.values(webAccessControlSchedule.subschedules).filter(
        n => !isEmpty(n)
      );
    }

    this.dom.$webAccessControlSchedulerForm.schedule = webAccessControlSchedule;
    this.dom.$webAccessControlSchedulerForm.propertiesSet = true;

    // Scroll to top of the form
    const contentElement = this.dom.$webAccessControlSchedulerForm.shadowRoot?.querySelector('.content');
    if (contentElement) {
      contentElement.scrollTop = 0;
    }
  }

  private injectDependenciesInChildren() {
    if (!this.dom.$webAccessControlSchedulerForm.injectDependencies) {
      return;
    }
    this.dom.$webAccessControlSchedulerForm.injectDependencies(this.dependencies);
    this.dom.$webAccessControlSchedulerForm.start().catch(console.error);
  }

  private setTranslations(): void {
    this.dom.$addScheduleBtn.textContent = translateFn('Add Scheduler');
    this.dom.$deleteBtn.textContent = translateFn('Delete');
  }
}
